// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { areAddressesTheSame } from "@insite/client-framework/Common/CustomerSelectors";
import { makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { createShipTo, updateBillTo, updateShipTo } from "@insite/client-framework/Services/CustomersService";
import { FulfillmentMethod, Session, updateSession } from "@insite/client-framework/Services/SessionService";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    AddOrUpdateShipTo,
    chain,
    UpdateBillTo,
    UpdateSession,
    ValidateBillingAddress,
} from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SaveShipping";
import validateBillingAddressForm from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/ValidateBillingAddressForm";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import { parseBoolean } from "@rpsc/XDL/Utilities";

export const xUpdateBillTo: typeof UpdateBillTo = async props => {
    const {
        getState,
        apiParameter: { cart },
    } = props;
    const {
        context: {
            session: { fulfillmentMethod },
        },
        pages: {
            onePageCheckout: { shippingAddressFormState, billingAddressFormState, isBillingAddressTheSameAsShipping },
        },
    } = getState();
    if (!shippingAddressFormState || !billingAddressFormState) {
        return;
    }

    const { address: localModifiedShipTo } = shippingAddressFormState;
    const localModifiedBillTo = { ...billingAddressFormState.address };
    if (isBillingAddressTheSameAsShipping && fulfillmentMethod === FulfillmentMethod.Ship) {
        // XNG-Changes: use updateShipToWithBillToFields custom mapper
        (props as any).xlocalModifiedShipTo = {
            ...localModifiedShipTo,
            ...updateShipToWithBillToFields(localModifiedBillTo),
        };
    } else {
        (props as any).xlocalModifiedShipTo = {
            ...localModifiedShipTo,
        };
    }
    const billTo = getBillToState(getState(), cart.billToId).value;
    if (!billTo) {
        throw new Error("There was no billTo loaded for the current cart");
    }

    if (areAddressesTheSame(billTo, localModifiedBillTo)) {
        props.updatedBillTo = billTo;
    } else {
        const updatedBillTo = await updateBillTo({ billTo: localModifiedBillTo, isEdit: false });
        props.updatedBillTo = { ...updatedBillTo, validation: billTo.validation };
        props.dispatch({
            type: "Pages/OnePageCheckout/SetBillingAddress",
            address: props.updatedBillTo,
        });
    }

    props.apiParameter.cart.billToId = props.updatedBillTo.id;
};

export const xAddOrUpdateShipTo: typeof AddOrUpdateShipTo = async props => {
    const { dispatch, getState, cart } = props;
    const state = getState();
    const {
        context: {
            session: { fulfillmentMethod },
        },
        pages: {
            onePageCheckout: { shippingAddressFormState, isBillingAddressTheSameAsShipping },
        },
    } = state;
    if (!shippingAddressFormState || fulfillmentMethod !== FulfillmentMethod.Ship) {
        return;
    }

    const shipTo = getShipToState(state, cart.shipToId).value;
    const billTo = getBillToState(state, cart.billToId).value;
    if (!shipTo || !billTo) {
        throw new Error("There was no shipTo or billTo loaded for the current cart");
    }

    const localModifiedShipTo = (props as any).xlocalModifiedShipTo;
    if (isBillingAddressTheSameAsShipping && cart.shipToId === cart.billToId) {
        props.dispatch({
            type: "Pages/OnePageCheckout/SetShippingAddress",
            address: localModifiedShipTo,
        });
        return;
    }

    if (isBillingAddressTheSameAsShipping && cart.shipToId !== cart.billToId && cart.billToId) {
        props.apiParameter.cart.shipToId = cart.billToId;
        const { shipToId } = props.apiParameter.cart;
        await makeHandlerChainAwaitable(loadShipTo)({
            shipToId,
            billToId: cart.billToId,
            expand: ["validation"],
        })(dispatch, getState);
        const shipToState = getShipToState(getState(), shipToId);
        shipToState.value &&
            props.dispatch({
                type: "Pages/OnePageCheckout/SetShippingAddress",
                address: shipToState.value,
            });
        return;
    }

    let updatedShipTo;
    if (localModifiedShipTo.isNew || (!isBillingAddressTheSameAsShipping && cart.shipToId === cart.billToId)) {
        updatedShipTo = await createShipTo({ shipTo: localModifiedShipTo });
    } else if (!areAddressesTheSame(shipTo, localModifiedShipTo)) {
        updatedShipTo = await updateShipTo({ shipTo: localModifiedShipTo, billToId: billTo.id });
    }

    if (updatedShipTo) {
        props.updatedShipTo = { ...updatedShipTo, validation: shipTo.validation };
        dispatch(loadShipTo({ shipToId: props.updatedShipTo.id, billToId: billTo.id }));
        props.dispatch({
            type: "Pages/OnePageCheckout/SetShippingAddress",
            address: props.updatedShipTo,
        });
        props.apiParameter.cart.shipToId = props.updatedShipTo.id;
    }
};

export const xUpdateSession: typeof UpdateSession = async props => {
    const {
        updatedShipTo,
        updatedBillTo,
        apiParameter: { cart },
    } = props;

    if (updatedShipTo || updatedBillTo) {
        await updateSession({
            session: {
                billToId: updatedBillTo?.id ?? cart.billToId,
                customerWasUpdated: true,
                ...(!parseBoolean(updatedShipTo?.properties.webOnly) &&
                    !updatedShipTo?.oneTimeAddress && { shipToId: updatedShipTo?.id ?? cart.shipToId }),
            } as any,
        });
    }
};

const updateShipToWithBillToFields = (billTo: BillToModel) => {
    return {
        firstName: billTo.firstName,
        lastName: billTo.lastName,
        attention: billTo.attention,
        companyName: billTo.companyName,
        address1: billTo.address1,
        address2: billTo.address2,
        address3: billTo.address3,
        address4: billTo.address4,
        country: billTo.country,
        city: billTo.city,
        state: billTo.state,
        postalCode: billTo.postalCode,
        phone: billTo.phone,
        email: billTo.email,
        fax: billTo.fax,
        contactFullName: billTo.contactFullName,
        fullAddress: billTo.fullAddress,
    };
};

const xValidateBillingAddress: typeof ValidateBillingAddress = async props => {
    const addressFieldsDataView = getAddressFieldsDataView(props.getState());
    const awaitableValidate = makeHandlerChainAwaitable<Parameters<typeof validateBillingAddressForm>[0], boolean>(
        validateBillingAddressForm,
    );
    const isBillingAddressValid = await awaitableValidate({
        address: props.updatedBillTo,
        validation: props.updatedBillTo.validation!,
        fieldDisplay: addressFieldsDataView.value!.shipToAddressFields,
    })(props.dispatch, props.getState);

    const {
        context: {
            session: { fulfillmentMethod },
        },
        pages: {
            onePageCheckout: { billingAddressFormState, shippingAddressFormState, isBillingAddressTheSameAsShipping },
        },
    } = props.getState();
    if (!isBillingAddressValid && isBillingAddressTheSameAsShipping && fulfillmentMethod === FulfillmentMethod.Ship) {
        const billingFormErrors = billingAddressFormState?.formErrors;
        const shippingFormErrors = { ...shippingAddressFormState?.formErrors };
        billingFormErrors &&
            shippingFormErrors &&
            Object.keys(billingFormErrors).forEach(key => {
                shippingFormErrors[key] = `${billingFormErrors[key]} ${translate("for billing")}`;
            });
        props.dispatch({
            type: "Pages/OnePageCheckout/SetShippingAddressFormErrors",
            formErrors: shippingFormErrors,
        });
        props.dispatch({
            type: "Pages/OnePageCheckout/CompleteSaveShipping",
        });

        return false;
    }

    props.dispatch({
        type: "Pages/OnePageCheckout/SetIsBillingAddressUpdateRequired",
        isBillingAddressUpdateRequired: !isBillingAddressValid,
    });
};

replaceInChain(chain, UpdateBillTo, xUpdateBillTo);
replaceInChain(chain, ValidateBillingAddress, xValidateBillingAddress);
replaceInChain(chain, AddOrUpdateShipTo, xAddOrUpdateShipTo);
replaceInChain(chain, UpdateSession, xUpdateSession);
